/**
 * artPlanAssetNames — the ONE authority for "which creation asset names does a
 * design's art plan cover", and therefore for which art a BUILD can redraw.
 *
 * Why this lives in the shared package rather than in the builder's plan
 * composer: build-time asset generation only ever visits PLANNED names
 * (`computeAssetRegenSet` loops the composed plan and checks the force list
 * inside that loop), while the studio lets a creator select any ATTACHED asset
 * and `requestBuildFn` used to validate against that attached set. Attached is
 * not a subset of planned — a studio-generated background the game binds by
 * name is attached and bound but absent from `scenery.backgrounds` — so an
 * approved `visualMigration` naming one was accepted, never visited, and the
 * build published the old art with no error, no gate note and nothing in the
 * trace (`bounty-bob-strikes-back` v13: `deep-shaft-bg` forced, `asset_gen`
 * came back `generated: []`). Three consumers now share this one derivation:
 * the builder's plan composer (which derives the names it generates from it),
 * `requestBuildFn` (which REFUSES a selection the plan cannot cover), and the
 * asset studio (which does not offer the redraw affordance for art outside it).
 *
 * The names are the render-manifest binding slugs, reserved in ONE namespace:
 * every design entity first (design order), then every scenery background,
 * because downstream the manifest's `assets` map is a single flat name→url
 * space and a scenery name that coincides with an entity name must not collapse
 * into it.
 */
import type { GameDesign } from './types.js';

/** Max sprites one style-locked hero sheet carries — ONE image call, sliced
 * into square 256 px cells on a near-square grid. Twelve lays out 4×4 (four
 * blank cells), i.e. 1024×1024: exactly the image model's ~1 MP output, so
 * every cell keeps its full source resolution. Real arcade casts need it (a
 * mine platformer's player, enemy, catwalks, ladders, chutes, transporters,
 * pickups and hatch are ten kinds). The plan composer slices heroes to it and
 * a composition presentation may not declare more kinds. */
export const HERO_SHEET_MAX_SPRITES = 12;

/** One creation asset name the design's art plan covers. */
export interface ArtPlanAssetName {
  /** The creation asset NAME the render-manifest binds (a unique valid slug). */
  name: string;
  /** What the plan draws under this name. */
  kind: 'sprite' | 'background';
  /** Index into `design.entities` (sprite) or `design.scenery.backgrounds`
   *  (background) — the plan entry this name was derived from. */
  index: number;
}

/**
 * Normalize any label to a valid CREATION asset slug (`isValidAssetName`):
 * lowercase, non-alphanumerics → hyphens, collapse + trim, cap 40. This is the
 * name the render-manifest binds. Returns '' when nothing usable survives.
 */
export function toAssetSlug(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
}

/**
 * Reserve a globally-unique creation slug against `used` (mutating it), deduping
 * a collision with a numeric suffix. Dedupe by TRIMMING THE BASE to fit the
 * suffix (never by slicing the suffixed string — at a 40-char slug that re-slices
 * the suffix off and loops forever; near 40 it leaves an invalid trailing
 * hyphen).
 */
function reserveSlug(used: Set<string>, base: string): string {
  let s = base;
  let n = 2;
  while (used.has(s)) {
    const suffix = `-${n++}`;
    s = `${base.slice(0, 40 - suffix.length)}${suffix}`;
  }
  used.add(s);
  return s;
}

/**
 * Every creation asset name the design's art plan covers, in plan order
 * (entities, then scenery backgrounds). Pure and deterministic: identical
 * design ⇒ identical list. Independent of world shape and of anything the
 * creation has attached — this is what the DESIGN plans, which is exactly the
 * set a build's generation can reach.
 */
export function artPlanAssetNames(design: GameDesign): ArtPlanAssetName[] {
  const used = new Set<string>();
  const out: ArtPlanAssetName[] = [];
  // Every drawable design entity is planned (generated or banked — both reserve
  // a name and both are reachable by a forced redraw).
  design.entities.forEach((entity, index) => {
    let slug = toAssetSlug(entity.name);
    if (slug.length < 2) slug = `entity-${index + 1}`;
    out.push({ name: reserveSlug(used, slug), kind: 'sprite', index });
  });
  (design.scenery?.backgrounds ?? []).forEach((bg, index) => {
    const slug = toAssetSlug(bg.name);
    // A name with nothing usable in it cannot be bound; the plan skips it.
    if (slug.length < 2) return;
    out.push({ name: reserveSlug(used, slug), kind: 'background', index });
  });
  return out;
}

/**
 * The attached art a BUILD can redraw: the intersection of what the creation
 * holds with what the design's art plan covers, matched case-insensitively like
 * every other name match in the asset path. Returns the caller's own asset
 * objects so the exact attached spelling is preserved.
 */
export function redrawableAssets<T extends { name?: string }>(
  design: GameDesign | undefined,
  attached: readonly T[],
): T[] {
  if (!design) return [];
  const planned = new Set(artPlanAssetNames(design).map((a) => a.name.toLowerCase()));
  return attached.filter((a) => planned.has((a.name ?? '').toLowerCase()));
}
